'use client';

import { useTransition } from 'react';

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@ps/ui/atoms/alert-dialog';
import { cn } from '@ps/ui/utils';

interface Props {
  open: boolean;
  onOpenChange: (next: boolean) => void;
  /** Current blocked state — drives the Block vs Unblock copy/direction. */
  blocked: boolean;
  memberName: string;
  /** Runs the toggle; resolves once the API call succeeds. */
  onConfirm: () => Promise<void>;
}

/**
 * Confirm blocking / unblocking a channel member — mirrors PopshopRN's
 * `ToggleBlockButton` confirm (destructive when blocking). Explicit dark colors
 * because the dashboard shell isn't `.dark`-scoped and `AlertDialog` portals to
 * `body` (sibling {@link DeleteChannelMessageDialog}).
 */
export function BlockMemberDialog({
  open,
  onOpenChange,
  blocked,
  memberName,
  onConfirm,
}: Props) {
  const [pending, startTransition] = useTransition();

  const handleConfirm = () => {
    if (pending) return;
    startTransition(async () => {
      try {
        await onConfirm();
        onOpenChange(false);
      } catch (error) {
        console.error('toggle channel member block failed', error);
      }
    });
  };

  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <AlertDialogContent className="text-content-1 border-line bg-surface-1">
        <AlertDialogHeader>
          <AlertDialogTitle className="text-content-1">
            {blocked ? 'Unblock user?' : 'Block user?'}
          </AlertDialogTitle>
          <AlertDialogDescription className="text-content-2">
            {blocked
              ? `${memberName} will be able to post in this channel again.`
              : `${memberName} will no longer be able to post in this channel.`}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel
            disabled={pending}
            className="text-content-1 border-line-strong bg-surface-1 hover:bg-brand-hover-soft"
          >
            Cancel
          </AlertDialogCancel>
          <AlertDialogAction
            disabled={pending}
            onClick={event => {
              // Keep the dialog mounted until the async toggle settles.
              event.preventDefault();
              handleConfirm();
            }}
            className={cn(
              'text-content-1',
              blocked
                ? 'bg-brand hover:bg-brand/90 text-content-on-brand'
                : 'bg-danger hover:bg-red-600',
            )}
          >
            {pending
              ? blocked
                ? 'Unblocking…'
                : 'Blocking…'
              : blocked
                ? 'Unblock'
                : 'Block'}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
